import { ENVIRONMENT } from '@/environments/environment.ts';
import { handleFetchErrorResponse, handleFetchUnexpectedError } from '@/lib/errorHandler.ts';
import { ErrorResponse } from '@/model/IResponse';
import { showSimpleErrorToast } from './use-sonner';

type RequestOptions = RequestInit & {
    headers?: Record<string, string>;
};

const SECURITY_CONFIG = {
    allowedMimeTypes: [
        /^application\/(pdf|vnd\..*|msword|vnd\.openxmlformats-officedocument\..+|octet-stream)$/i,
        /^image\/(jpeg|jpg|png|gif|webp|svg\+xml|bmp|tiff)$/i,
        /^text\/(csv|plain)$/i,
        /^audio\/(mpeg|wav|ogg|mp3)$/i,
        /^video\/(mp4|avi|mov|wmv)$/i
    ],
    forbiddenMimeTypes: [
        /^text\/html$/i,
        /^application\/(javascript|ecmascript|xml)$/i,
        /^text\/(xml|javascript|ecmascript|html)$/i,
        /^application\/x-javascript$/i,
        /^application\/x-shockwave-flash$/i,
        /^application\/x-msdownload$/i
    ],
    maxFileNameLength: 255,
    maxFileSize: 100 * 1024 * 1024,
    allowedDomains: [],
    blockedDomains: ['localhost', '127.0.0.1', '0.0.0.0']
};

async function customFetch<TResponse extends {}>(endpoint: string, options: RequestOptions = {}) {
    const commonHeaders = getCommonRequestHeaders();
    const headers = {
        ...commonHeaders,
        ...options.headers,
    };

    if (headers['Content-Type'] === null) {
        delete headers['Content-Type'];
    } else if (headers && !headers.hasOwnProperty('Content-Type') && !(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }

    try {
        const response = await fetch(`${endpoint}`, {
            ...options,
            headers,
            credentials: 'include',
        } as RequestInit);

        if (!response.ok) {
            const errorData = await handleFetchErrorResponse(response);

            showSimpleErrorToast({
                title: errorData.title,
                description: errorData.errorMessage,
            });

            return errorData;
        }

        const contentType = response.headers.get('Content-Type') || '';
        const data = await (
            contentType.toLowerCase().includes('application/octet-stream')
                ? response.blob()
                : response.json()
        );

        return data as TResponse;
    } catch (error: unknown) {
        const e = error as ErrorResponse;
        const errorData = await handleFetchUnexpectedError(e);

        showSimpleErrorToast({
            title: errorData.title,
            description: errorData.errorMessage,
        });

        return errorData;
    }
}

export default customFetch;

function validateUrl(url: string): boolean {
    try {
        const parsedUrl = new URL(url);

        if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
            return false;
        }

        const hostname = parsedUrl.hostname.toLowerCase();
        const isBlocked = SECURITY_CONFIG.blockedDomains.some(blocked => hostname === blocked || hostname.endsWith('.' + blocked));
        if (isBlocked) {
            return false;
        }

        if (SECURITY_CONFIG.allowedDomains.length > 0) {
            return SECURITY_CONFIG.allowedDomains.some(allowed => hostname === allowed || hostname.endsWith('.' + allowed));
        }

        return true;
    } catch {
        return false;
    }
}

function validateMimeType(mimeType: string): { isAllowed: boolean; isForbidden: boolean } {
    const normalizedType = mimeType.toLowerCase().trim();

    const isForbidden = SECURITY_CONFIG.forbiddenMimeTypes.some(pattern => pattern.test(normalizedType));
    const isAllowed = SECURITY_CONFIG.allowedMimeTypes.some(pattern => pattern.test(normalizedType));

    return { isAllowed, isForbidden };
}

function sanitizeFileName(fileName: string): string {
    return fileName
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .substring(0, SECURITY_CONFIG.maxFileNameLength)
        .replace(/^\.+/, '')
        .replace(/\.+$/, '') || 'download';
}

function validateFileSize(blob: Blob): boolean {
    return blob.size <= SECURITY_CONFIG.maxFileSize;
}

export async function fetchPost<TResponse, TBody extends {}>(url: string, body?: TBody) {
    const requestOptions: RequestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    };
    return await customFetch<TResponse>(url, requestOptions);
}

export async function fetchPut<TResponse, TBody extends {}>(url: string, body?: TBody) {
    const requestOptions: RequestOptions = {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    };
    return await customFetch<TResponse>(url, requestOptions);
}

export async function fetchDelete<TResponse, TBody extends {}>(url: string, body?: TBody) {
    const requestOptions: RequestOptions = {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    };
    return await customFetch<TResponse>(url, requestOptions);
}

export async function login(accessToken: string) {
    if (!accessToken || typeof accessToken !== 'string') {
        throw new Error('Invalid access token provided');
    }

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
    };

    const endpoint = `${ENVIRONMENT.authApiPath.basePath}/user/SetAuthCookie`;

    try {
        const response = await fetch(endpoint, {
            method: 'GET',
            headers,
            credentials: 'include',
        } as RequestInit);

        if (!response.ok) {
            const errorData = await handleFetchErrorResponse(response);

            showSimpleErrorToast({
                title: errorData.title,
                description: errorData.errorMessage,
            });

            return errorData;
        }

        return response;
    } catch (error: unknown) {
        const e = error as ErrorResponse;
        const errorData = await handleFetchUnexpectedError(e);

        showSimpleErrorToast({
            title: errorData.title,
            description: errorData.errorMessage,
        });

        return errorData;
    }
}

function getCommonRequestHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const filters = localStorage.getItem('filters');
    const databaseName = getDatabaseName();

    if (databaseName) {
        headers['X-Tenant-Id'] = databaseName;
    }
    const languageId = localStorage.getItem('cultureId');
    if (languageId) {
        headers['X-Language'] = languageId;
    }

    if (filters) {
        headers['X-Access-Filters'] = filters;
    }

    return headers;
}

function getClientToken(): string | undefined {
    return localStorage.getItem('client-token') ?? undefined;
}

function getOnePlaceToken(): string | undefined {
    return localStorage.getItem('oneplace-token') ?? undefined;
}

function getDatabaseName(): string | undefined {
    return localStorage.getItem('database-name') ?? undefined;
}

function getDownloadHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    const clientTokenValue = getClientToken();
    const filters = localStorage.getItem('filters');
    const onePlaceTokenValue = getOnePlaceToken();
    const databaseName = getDatabaseName();

    if (databaseName) {
        headers['X-Tenant-Id'] = databaseName;
    }
    const languageId = localStorage.getItem('cultureId');
    if (languageId) {
        headers['X-Language'] = languageId;
    }
    if (clientTokenValue) {
        headers.Authorization = `Bearer ${clientTokenValue}`;
    }
    if (filters) {
        headers['X-Access-Filters'] = filters;
    }
    if (onePlaceTokenValue) {
        headers['X-OnePlace-Token'] = `Bearer ${onePlaceTokenValue}`;
    }
    return headers;
}

export async function downloadFile(fileUrl: string, fileName: string): Promise<void> {
  try {
    if (!validateUrl(fileUrl)) {
      throw new Error('URL inválida ou não permitida por motivos de segurança.');
    }

    const safeFileName = sanitizeFileName(fileName);

    const headers = getDownloadHeaders();
    const response = await fetch(fileUrl, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await handleFetchErrorResponse(response);
      showSimpleErrorToast({
        title: errorData.title,
        description: errorData.errorMessage,
      });
      throw new Error(errorData.errorMessage || 'Falha ao baixar arquivo.');
    }

    const contentTypeHeader = response.headers.get('Content-Type') || '';
    const contentLengthHeader = response.headers.get('Content-Length');
    const contentDisposition = response.headers.get('Content-Disposition');

    const mimeCheck = validateMimeType(contentTypeHeader);
    if (mimeCheck.isForbidden) {
      throw new Error('Tipo de arquivo proibido por motivos de segurança.');
    }
    if (!mimeCheck.isAllowed) {
      throw new Error('Tipo de arquivo não permitido.');
    }

    if (contentLengthHeader) {
      const len = parseInt(contentLengthHeader, 10);
      if (!Number.isNaN(len) && len > SECURITY_CONFIG.maxFileSize) {
        throw new Error(`Arquivo muito grande. Máximo permitido: ${SECURITY_CONFIG.maxFileSize / (1024 * 1024)} MB`);
      }
    }

    const blob = await response.blob();

    if (!validateFileSize(blob)) {
      throw new Error(`Arquivo muito grande. Máximo permitido: ${SECURITY_CONFIG.maxFileSize / (1024 * 1024)} MB`);
    }

    const actualMime = (blob.type || '').toLowerCase();
    const mimeValidation = validateMimeType(actualMime);
    if (mimeValidation.isForbidden) {
      throw new Error('Tipo de arquivo proibido por motivos de segurança.');
    }
    if (!mimeValidation.isAllowed) {
      const sampleSize = Math.min(1024, blob.size);
      try {
        const sample = await blob.slice(0, sampleSize).text();
        const lowered = sample.toLowerCase();
        if (lowered.includes('<script') || lowered.includes('<!doctype html') || lowered.includes('<html') || lowered.includes('javascript:')) {
          throw new Error('Conteúdo suspeito detectado no arquivo (possível HTML/JS mascarado).');
        }
      } catch (err) {
        throw new Error('Tipo de arquivo não permitido.');
      }
    }

    const cdName = parseContentDisposition(contentDisposition);
    const finalFileName = sanitizeFileName(cdName || safeFileName);
    if ('showSaveFilePicker' in window) {
      try {
        // @ts-ignore
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: finalFileName,
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch (err) {
      }
    }
    await downloadFileSecure(blob, finalFileName);

  } catch (error) {
    console.error('Erro ao baixar arquivo:', error);
    const message = error instanceof Error ? error.message : 'Falha no download. Tente novamente.';
    throw new Error(message);
  }
}

async function downloadFileSecure(blob: Blob, safeFileName: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  let link: HTMLAnchorElement | null = null;
  try {
    link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', safeFileName);
    link.setAttribute('rel', 'noopener noreferrer nofollow');
    link.setAttribute('target', '_self');
    link.setAttribute('referrerpolicy', 'no-referrer');
    link.setAttribute('aria-hidden', 'true');

    link.style.position = 'fixed';
    link.style.left = '-9999px';
    link.style.top = '-9999px';
    link.style.width = '1px';
    link.style.height = '1px';
    link.style.overflow = 'hidden';

    document.body.appendChild(link);
    await new Promise((res) => setTimeout(res, 10));
    link.click();
    document.body.removeChild(link);
    link = null;
  } finally {
    setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) { /* ignore */ }
    }, 200);
    try {
      if (link && link.parentNode) {
        link.parentNode.removeChild(link);
      }
    } catch (_){
  }
  }
}

function parseContentDisposition(header: string | null): string | null {
  if (!header) return null;

  try {
    const utf8Match = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      return decodeURIComponent(utf8Match[1].trim());
    }
    const asciiMatch = header.match(/filename\s*=\s*["']?([^"';]+)["']?/i);
    if (asciiMatch && asciiMatch[1]) {
      return asciiMatch[1].trim();
    }
  } catch {}

  return null;
}